import { Star, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FastReveal } from "@/components/FastReveal";
import { useLanguage } from "@/context/LanguageContext";
import { reviews, features } from "@/config/site";

// -----------------------------------------------------------------------------
// Reviews — banda de reseñas verificadas.
// Solo se renderiza cuando features.reviews === true y existe al menos 1 reseña.
// Diseño alineado con Design Bible §Reviews & Social Proof System:
//   - Autenticidad (verificadas, nombres reales, historia específica).
//   - Restraint (sin ratings inflados, sin promociones, sin popups).
//   - Contexto (producto y fecha visibles).
//   - Accesibilidad completa (aria-labels, semantics).
// -----------------------------------------------------------------------------

const EASE = [0.22, 1, 0.36, 1];

// Renderiza estrellas con role="img" y aria-label del rating exacto
function StarRating({ value, label }) {
  return (
    <div className="flex items-center gap-0.5" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${
            i <= value ? "fill-[#111] text-[#111]" : "fill-transparent text-[#d4d4d0]"
          }`}
          strokeWidth={1.4}
        />
      ))}
    </div>
  );
}

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Reviews() {
  const { t, lang } = useLanguage();

  // Solo se muestra si el flag está activo y hay reseñas verificadas
  if (!features.reviews) return null;
  const verified = reviews.filter((r) => r.verified);
  if (verified.length === 0) return null;

  // Rating agregado (promedio real, sin manipular)
  const avg = verified.reduce((s, r) => s + r.rating, 0) / verified.length;
  const avgFmt = Number.isInteger(avg) ? avg.toFixed(0) : avg.toFixed(1);

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 lg:px-10">
        {/* Encabezado con rating agregado real */}
        <Reveal className="mb-12 text-center">
          <span className="eyebrow text-[11px] text-[#999]">{t.reviews.eyebrow}</span>
          <h2 className="mt-3 font-serif-display text-3xl font-medium text-[#111] sm:text-4xl">
            {t.reviews.title}
          </h2>
          <p className="mx-auto mt-3 max-w-md font-body text-sm text-[#777]">
            {t.reviews.subtitle}
          </p>
          <div className="mt-5 inline-flex items-center gap-3">
            <StarRating value={Math.round(avg)} label={t.reviews.ratingLabel(avgFmt)} />
            <span
              data-testid="reviews-avg"
              className="font-body text-sm font-medium text-[#333]"
            >
              {avgFmt} · {verified.length}
            </span>
          </div>
        </Reveal>

        {/* Grid de reseñas — stagger elegante */}
        <ul className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {verified.map((r, i) => (
            <FastReveal
              key={r.id}
              as="li"
              delay={i * 0.08}
              y={20}
              data-testid={`review-${r.id}`}
              className="card-lift flex h-full flex-col rounded-2xl border border-[#eee] bg-[#fafaf8] p-6"
            >
              <div className="flex items-center justify-between">
                <StarRating value={r.rating} label={t.reviews.ratingLabel(r.rating)} />
                <span className="font-body text-xs text-[#999]">
                  {formatDate(r.date, lang)}
                </span>
              </div>
              <blockquote className="mt-4 flex-1 font-body text-[15px] leading-relaxed text-[#333]">
                <span aria-hidden="true" className="font-serif-display text-2xl leading-none text-[#c9c9c2]">&ldquo;</span>
                {r.quote?.[lang] || r.quote?.en}
              </blockquote>
              <div className="mt-5 flex items-center gap-3 border-t border-[#e8e8e8] pt-4">
                <div
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white font-body text-xs font-semibold text-[#111] ring-1 ring-[#e2e2e2]"
                >
                  {r.initials}
                </div>
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-[#111]">{r.name}</p>
                  {r.product && (
                    <p className="font-body text-[11px] text-[#888]">
                      {r.product?.[lang] || r.product?.en}
                    </p>
                  )}
                </div>
                <span
                  className="inline-flex items-center gap-1 font-body text-[10px] font-medium uppercase tracking-wider text-[#666]"
                  title={t.reviews.verified}
                >
                  <BadgeCheck className="h-3.5 w-3.5 text-[#111]" strokeWidth={1.6} />
                  {t.reviews.verified}
                </span>
              </div>
            </FastReveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
